/**
 * Host-process diagnostics for opaque Chromium launch failures.
 *
 * @module playwright-client/launch-host-diagnostics
 */
import { fstatSync } from 'node:fs';

export type BrowserLaunchFileDescriptor =
  | { fd: number; state: 'open'; kind: string }
  | { fd: number; state: 'closed'; errorCode?: string };

export type BrowserLaunchHostDiagnostics = {
  pid: number;
  ppid: number;
  platform: NodeJS.Platform;
  runtime: 'bun' | 'node';
  execPath: string;
  transportDescriptorCollisionRisk: boolean;
  transportDescriptors: BrowserLaunchFileDescriptor[];
};

export function inspectBrowserLaunchFileDescriptor(fd: number): BrowserLaunchFileDescriptor {
  try {
    const stats = fstatSync(fd);
    const kind = stats.isDirectory()
      ? 'directory'
      : stats.isFIFO()
        ? 'fifo'
        : stats.isSocket()
          ? 'socket'
          : stats.isFile()
            ? 'file'
            : 'other';
    return { fd, state: 'open', kind };
  } catch (error) {
    const code = (error as NodeJS.ErrnoException).code;
    return { fd, state: 'closed', ...(code ? { errorCode: code } : {}) };
  }
}

export function collectBrowserLaunchHostDiagnostics(): BrowserLaunchHostDiagnostics {
  const transportDescriptors = [3, 4].map(inspectBrowserLaunchFileDescriptor);
  return {
    pid: process.pid,
    ppid: process.ppid,
    platform: process.platform,
    runtime: process.versions.bun ? 'bun' : 'node',
    execPath: process.execPath,
    transportDescriptorCollisionRisk: transportDescriptors.some(
      (descriptor) => descriptor.state === 'open' && descriptor.kind === 'directory'
    ),
    transportDescriptors,
  };
}
